import React, { useEffect, useState } from 'react';
import { Rocket, X, Sparkles, BookOpen, Download } from 'lucide-react';

/**
 * Personalised onboarding card — shown once, first time a user lands on their
 * dashboard. Opens the role-appropriate Quick-Start manual with a single click
 * and marks the flag as seen so it never shows again.
 *
 * Props (optional):
 *   role: string — override the auto-detected role from /api/me/onboarding.
 */
export default function OnboardingCard({ role }) {
  const [state, setState] = useState(null); // { role, recommended_manual_url, recommended_manual_title }
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/me/onboarding`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && !data.onboarding_seen) {
          setState({
            ...data,
            role: role || data.role,
          });
        }
      } catch (_) {
        /* silent — non-critical UX */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [BACKEND, role]);

  const dismiss = async () => {
    setDismissed(true);
    try {
      await fetch(`${BACKEND}/api/me/onboarding/dismiss`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_) { /* best-effort */ }
  };

  if (loading || dismissed || !state) return null;

  const roleLabels = {
    worker: 'Worker',
    employer: 'Employer',
    agent: 'Agency',
    admin: 'Administrator',
  };
  const roleColors = {
    worker: 'from-cyan-500 to-teal-500',
    employer: 'from-violet-500 to-fuchsia-500',
    agent: 'from-orange-500 to-red-500',
    admin: 'from-rose-500 to-red-600',
  };
  const label = roleLabels[state.role] || 'Worker';
  const gradient = roleColors[state.role] || roleColors.worker;
  const pdfUrl = `${BACKEND}${state.recommended_manual_url}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="onboarding-card-overlay"
      onClick={dismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-card"
      >
        <div className={`bg-gradient-to-r ${gradient} p-6 text-white relative`}>
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition"
            aria-label="Dismiss"
            data-testid="onboarding-dismiss-x"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold uppercase tracking-wider text-white/90">
            <Sparkles className="w-4 h-4" />
            Welcome to ScensobConnect
          </div>
          <h2 className="text-2xl font-bold mb-1" data-testid="onboarding-headline">
            Your {label} Quick-Start is ready
          </h2>
          <p className="text-sm text-white/90">
            7-page guide · every core flow explained · about 10 minutes to read
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 mb-4">
            <div className="p-2 rounded-lg bg-white shadow-sm">
              <Rocket className="w-5 h-5 text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {state.recommended_manual_title}
              </p>
              <p className="text-xs text-slate-500">
                Downloads as PDF. Also available any time from Help & Training.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              onClick={dismiss}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition"
              data-testid="onboarding-open-quickstart"
            >
              <BookOpen className="w-4 h-4" />
              Open Quick-Start
            </a>
            <a
              href={pdfUrl}
              download={`scensobconnect-${state.role}-quickstart.pdf`}
              onClick={dismiss}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold transition"
              data-testid="onboarding-download-quickstart"
              title="Download PDF"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              onClick={dismiss}
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm font-medium transition"
              data-testid="onboarding-skip"
            >
              Skip
            </button>
          </div>

          <p className="mt-4 text-[11px] text-slate-400 text-center">
            You&apos;ll only see this card once. Find every manual under Help &amp; Training.
          </p>
        </div>
      </div>
    </div>
  );
}
